import {
  CheckBox,
  FieldSet,
  FormContext,
  FormState,
  InfoBlockLayout,
  InfoBlockStatus,
  InputWizardPanelComponent,
  TextInput,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { AdbsDbVersionSelect } from "../../components/AdbsDbVersionSelect/AdbsDbVersionSelect";
import { IntegerInput } from "../../components/IntegerInput/IntegerInput";
import { LicenseTypeSelect } from "../../components/LicenseTypeSelect/LicenseTypeSelect";
import { WorkloadSelect } from "../../components/WorkloadSelect/WorkloadSelect";
import { AdbDbVersionSummaryDbWorkloadEnum } from "../../gen/clients/mchub-azure-api-client-adb";
import { IdResourceType } from "../../helpers/idHelper";
import {
  adbsMaxCPUCount,
  adbsMaxStorage,
  adbsMinCPUCount,
  adbsMinStorage,
  AdbWorkloadType,
} from "../../helpers/resourceHelper";
import { validateAdbDBName } from "../../helpers/validationHelper";

export const GROUP_CONFIG = "config";

export enum Fields {
  StorageSize = "adbsDatabaseDetails.dataStorageSizeInTBs",
  DatabaseName = "adbsDatabaseDetails.dbName",
  CoreCount = "adbsDatabaseDetails.cpuCoreCount",
  AutoScaling = "adbsDatabaseDetails.isAutoScalingEnabled",
  WorkloadType = "adbsDatabaseDetails.dbWorkload",
  DbVersion = "adbsDatabaseDetails.dbVersion",
  LicenseType = "adbsDatabaseDetails.licenseModel",
}
export enum DEFAULT_VALUES {
  OCPU_COUNT = 1,
  STORAGE_SIZE = 1
}

export enum FieldTestIds {
  DbVersion = "db-version",
  DbWorkload = "db-workload",
  DbLicenseType = "db-license-type",
  DbName = "db-name",
}

export interface ConfigTabContentProps {
  location: string;
  subscription: string;
  inputWizardPanelRef: InputWizardPanelComponent;
  isApex?: boolean;
}

const DEFAULT_WORKLOAD_TYPE = AdbWorkloadType.DataWarehouse;

export const ConfigTabContent = ({
  inputWizardPanelRef,
  subscription,
  location,
  isApex,
}: ConfigTabContentProps): JSX.Element => {
  const form: FormState = React.useContext(FormContext);
  const [workload, setWorkload] = React.useState<string>(DEFAULT_WORKLOAD_TYPE);
  const [licenseTypeKey, setLicenseTypeKey] = React.useState<string>(uniqueGUID());

  const onError = (): void => inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.general());
  const onMissingDependencies = (missingDependencies: IdResourceType[]): void => {
    const dependencyType = missingDependencies?.[0];
    if (dependencyType === IdResourceType.SUBSCRIPTIONS || dependencyType === IdResourceType.LOCATION) {
      inputWizardPanelRef.showErrorDialog(
        Messages.createCommon.dependencyWarnings.basicsIncomplete.message(),
        Messages.createCommon.dependencyWarnings.basicsIncomplete.title(),
      );
    }
  };

  return (
    <>
      <FieldSet header="">
        {!isApex && (
          <WorkloadSelect
            required
            fieldName={Fields.WorkloadType}
            label={Messages.labels.workloadType()}
            testId={FieldTestIds.DbWorkload}
            onChange={value => {
              setWorkload(value);
              form.setValue(undefined, Fields.LicenseType, GROUP_CONFIG);
              setLicenseTypeKey(uniqueGUID());
            }}
            defaultValue={[workload]}
          />
        )}
        <IntegerInput
          min={adbsMinCPUCount}
          max={adbsMaxCPUCount}
          defaultValue={DEFAULT_VALUES.OCPU_COUNT}
          required
          label={Messages.labels.ocpuCount()}
          fieldName={Fields.CoreCount}
        />
        <CheckBox
          fieldName={Fields.AutoScaling}
          label={Messages.labels.ocpuAutoScaling()}
          defaultValue
          statusInfo={{
            messageType: InfoBlockStatus.INFO,
            infoLayout: InfoBlockLayout.Compact,
            message: Messages.hints.ocpuAutoScaling(),
          }}
        />
        <IntegerInput
          min={adbsMinStorage}
          max={adbsMaxStorage}
          defaultValue={DEFAULT_VALUES.STORAGE_SIZE}
          required
          label={Messages.labels.storageInTB()}
          fieldName={Fields.StorageSize}
        />
        {!isApex && (
          <LicenseTypeSelect
            key={licenseTypeKey}
            testId={FieldTestIds.DbLicenseType}
            fieldName={Fields.LicenseType}
            defaultValue={(workload === AdbWorkloadType.APEX || workload === AdbWorkloadType.JSON)
              ? "LICENSE_INCLUDED" : undefined}
            disabled={workload === AdbWorkloadType.APEX || workload === AdbWorkloadType.JSON}
          />
        )}
      </FieldSet>
      <FieldSet header={Messages.createAdbs.configurationTab.sectionTitles.databaseDetails()}>
        <AdbsDbVersionSelect
          required
          label={Messages.labels.databaseVersion()}
          testId={FieldTestIds.DbVersion}
          fieldName={Fields.DbVersion}
          location={location}
          subscriptionId={subscription}
          workload={workload as AdbDbVersionSummaryDbWorkloadEnum}
          onError={onError}
          onMissingDependencies={onMissingDependencies}
        />
        <TextInput
          required
          testId={FieldTestIds.DbName}
          fieldName={Fields.DatabaseName}
          label={Messages.labels.databaseName()}
          validator={validateAdbDBName}
        />
      </FieldSet>
    </>
  );
};
